// Miky Motorsport - audio (Web Audio synth): zvuk motoru (RPM), SFX, jednoducha BGM
// POZN: AudioContext se musi resume() po user gestu (klik v menu). Testovani v headless
// Chrome neprehraje zvuk, ale kod je bezpecny (guardy) - na realnem stroji hraje.

export class AudioEngine {
  constructor() {
    this.ctx = null;
    this.ready = false;
    this.muted = false;
    this._musicTimer = null;
    this._masterVolume = 0.6; // aplikovany master gain (default ~ puvodni hardcoded 0.6)
  }

  // Volat po prvnim user gestu. volume = ulozene profile.settings.masterVolume (0..1);
  // undefined => ponech stavajici (default 0.6-ekvivalent).
  init(volume) {
    if (volume != null) this._masterVolume = volume;
    if (this.ready) {
      if (this.ctx.state === 'suspended') this.ctx.resume();
      return;
    }
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    this.ctx = new AC();
    const ctx = this.ctx;

    this.master = ctx.createGain();
    this.master.gain.value = this.muted ? 0 : this._masterVolume;
    this.master.connect(ctx.destination);

    this.sfxBus = ctx.createGain();
    this.sfxBus.gain.value = 0.9;
    this.sfxBus.connect(this.master);

    this.musicBus = ctx.createGain();
    this.musicBus.gain.value = 0.0;
    this.musicBus.connect(this.master);

    // --- Engine: 3 oscilatory + lowpass + gain ---
    this.engineGain = ctx.createGain();
    this.engineGain.gain.value = 0.0001;
    this.engineLP = ctx.createBiquadFilter();
    this.engineLP.type = 'lowpass';
    this.engineLP.frequency.value = 600;
    this.engineGain.connect(this.engineLP);
    this.engineLP.connect(this.master);

    this.oscs = [];
    const ratios = [1, 0.5, 2, 1.01];
    const types = ['sawtooth', 'square', 'sawtooth', 'sawtooth'];
    const gains = [0.5, 0.35, 0.18, 0.3];
    for (let i = 0; i < ratios.length; i++) {
      const o = ctx.createOscillator();
      o.type = types[i];
      o.frequency.value = 60;
      const g = ctx.createGain();
      g.gain.value = gains[i];
      o.connect(g);
      g.connect(this.engineGain);
      o.start();
      this.oscs.push({ o, ratio: ratios[i] });
    }
    this._engineFreq = 60;

    this.ready = true;
  }

  setMuted(m) {
    this.muted = m;
    if (this.master) this.master.gain.value = m ? 0 : this._masterVolume;
  }

  // Nastav master hlasitost (0..1) z profile.settings.masterVolume. Respektuje mute.
  setMasterVolume(v) {
    this._masterVolume = Math.max(0, Math.min(1, v));
    if (this.master && !this.muted) this.master.gain.value = this._masterVolume;
  }

  // Volat kazdy frame s RPM a plynem (0..1). engineOn=false v menu/garazi.
  updateEngine(rpm, throttle, on) {
    if (!this.ready) return;
    const ctx = this.ctx;
    if (!on) {
      this.engineGain.gain.setTargetAtTime(0.0001, ctx.currentTime, 0.1);
      return;
    }
    // frekvence z RPM (idle ~45 Hz -> redline ~190 Hz)
    const f = 42 + Math.max(0, (rpm - 800)) / 5800 * 150;
    this._engineFreq += (f - this._engineFreq) * 0.25; // hladke
    for (const e of this.oscs) {
      e.o.frequency.setTargetAtTime(this._engineFreq * e.ratio, ctx.currentTime, 0.02);
    }
    // A3: na liftu (nizky plyn) + vysokem rpm je slyset brzdeni motorem (overrun), ne ticho.
    const tN = Math.min(1, Math.max(0, throttle));
    const rpmN = Math.min(1, Math.max(0, (rpm - 900) / 5700));
    const vol = 0.035 + tN * 0.20 + (1 - tN) * rpmN * 0.05;
    this.engineGain.gain.setTargetAtTime(vol, ctx.currentTime, tN < 0.05 ? 0.04 : 0.06);
    this.engineLP.frequency.setTargetAtTime(480 + tN * 2200 + rpmN * 1500, ctx.currentTime, 0.05);
  }

  // --- SFX ---
  _tone(freq, dur, type = 'square', vol = 0.3, slideTo = null) {
    if (!this.ready) return;
    const ctx = this.ctx;
    const o = ctx.createOscillator();
    o.type = type;
    o.frequency.value = freq;
    if (slideTo) o.frequency.exponentialRampToValueAtTime(slideTo, ctx.currentTime + dur);
    const g = ctx.createGain();
    g.gain.value = vol;
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + dur);
    o.connect(g);
    g.connect(this.sfxBus);
    o.start();
    o.stop(ctx.currentTime + dur + 0.02);
  }

  beep(high = false) { this._tone(high ? 880 : 560, high ? 0.4 : 0.18, 'square', 0.35); }
  countdownGo() { this._tone(660, 0.5, 'square', 0.4, 1320); }

  thud(strength = 1) {
    if (!this.ready) return;
    const ctx = this.ctx;
    // sum noise burst
    const len = 0.18;
    const buf = ctx.createBuffer(1, ctx.sampleRate * len, ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / d.length);
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const g = ctx.createGain();
    g.gain.value = Math.min(0.6, 0.25 + strength * 0.35);
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 400;
    src.connect(lp); lp.connect(g); g.connect(this.sfxBus);
    src.start();
  }

  click() { this._tone(420, 0.06, 'square', 0.2); }

  // A2: krátký blip při řazení - nahoru = vyšší cvak, dolů = nižší.
  gearShift(up = true) {
    if (up) this._tone(760, 0.06, 'square', 0.12, 980);
    else this._tone(520, 0.07, 'square', 0.11, 360);
  }

  // A4: krátký "svist" při těsném minutí auta - šumový burst přes stoupající bandpass.
  whoosh() {
    if (!this.ready) return;
    const ctx = this.ctx;
    const len = 0.22;
    const buf = ctx.createBuffer(1, ctx.sampleRate * len, ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < d.length; i++) { const t = i / d.length; d[i] = (Math.random() * 2 - 1) * Math.sin(Math.PI * t); }
    const src = ctx.createBufferSource(); src.buffer = buf;
    const bp = ctx.createBiquadFilter(); bp.type = 'bandpass'; bp.Q.value = 0.8;
    bp.frequency.setValueAtTime(450, ctx.currentTime);
    bp.frequency.exponentialRampToValueAtTime(2600, ctx.currentTime + len);
    const g = ctx.createGain(); g.gain.value = 0.16;
    src.connect(bp); bp.connect(g); g.connect(this.sfxBus);
    src.start();
  }

  // Kontinualni pisko/skret pneu pri smyku. intensity 0..1 (0 = ticho).
  // surfaceGrip (A3): 1.0 asfalt = jasny squeal, 0.9 kerb = tlumeneji, 0.45 trava = nizky scrub.
  // Lazy: jeden loopovany sumovy zdroj -> bandpass -> gain, gain se rampuje dle intenzity.
  setSkid(intensity, surfaceGrip = 1) {
    if (!this.ready) return;
    const ctx = this.ctx;
    if (!this._skid) {
      const len = 2;
      const buf = ctx.createBuffer(1, ctx.sampleRate * len, ctx.sampleRate);
      const d = buf.getChannelData(0);
      for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
      const src = ctx.createBufferSource();
      src.buffer = buf; src.loop = true;
      const bp = ctx.createBiquadFilter();
      bp.type = 'bandpass'; bp.frequency.value = 1500; bp.Q.value = 1.4;
      const g = ctx.createGain(); g.gain.value = 0.0001;
      src.connect(bp); bp.connect(g); g.connect(this.sfxBus);
      src.start();
      this._skid = { g, bp };
    }
    const i = Math.max(0, Math.min(1, intensity));
    const target = i > 0.02 ? 0.03 + i * 0.17 : 0.0001;
    this._skid.g.gain.setTargetAtTime(target, ctx.currentTime, 0.05);
    // A3: charakter dle povrchu - asfalt jasny squeal, trava nizky scrub (nizsi freq + sirsi Q)
    const bright = surfaceGrip >= 0.95 ? 1 : surfaceGrip >= 0.85 ? 0.65 : 0;
    const freq = 600 + bright * 650 + i * (400 + 550 * bright);
    this._skid.bp.frequency.setTargetAtTime(freq, ctx.currentTime, 0.08);
    this._skid.bp.Q.setTargetAtTime(0.8 + bright * 0.6, ctx.currentTime, 0.1);
  }

  // --- Jednoducha BGM (rytmicky pad + bass loop) ---
  startMusic() {
    if (!this.ready || this._musicTimer) return;
    this.musicBus.gain.setTargetAtTime(0.18, this.ctx.currentTime, 1.0);
    const scale = [0, 3, 5, 7, 10]; // moll pentatonika
    const root = 110; // A2
    let step = 0;
    const beat = 0.32;
    const tick = () => {
      if (!this.ready) return;
      const ctx = this.ctx;
      const t = ctx.currentTime;
      // bass
      if (step % 2 === 0) this._musNote(root * Math.pow(2, scale[(step / 2) % scale.length] / 12) / 2, beat * 1.8, 'triangle', 0.18, this.musicBus);
      // arp
      const n = scale[(step * 2) % scale.length];
      this._musNote(root * 2 * Math.pow(2, n / 12), beat * 0.9, 'square', 0.06, this.musicBus);
      step++;
    };
    this._musicTimer = setInterval(tick, beat * 1000);
  }
  stopMusic() {
    if (this._musicTimer) { clearInterval(this._musicTimer); this._musicTimer = null; }
    if (this.musicBus) this.musicBus.gain.setTargetAtTime(0.0, this.ctx.currentTime, 0.6);
  }
  _musNote(freq, dur, type, vol, bus) {
    const ctx = this.ctx;
    const o = ctx.createOscillator();
    o.type = type; o.frequency.value = freq;
    const g = ctx.createGain();
    g.gain.value = 0; g.gain.linearRampToValueAtTime(vol, ctx.currentTime + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + dur);
    o.connect(g); g.connect(bus);
    o.start(); o.stop(ctx.currentTime + dur + 0.02);
  }
}
